import { SxProps, Theme, alpha } from '@mui/material/styles';
import mapValues from 'lodash/mapValues';

type BackgroundBlurProps = {
  blur?: number;
  opacity?: number;
  color?: string;
};

type BackgroundGradientProps = {
  direction?: string;
  startColor?: string;
  endColor?: string;
};

interface BackgroundImageProps extends BackgroundGradientProps {
  url?: string;
}

function getDirection(value = 'bottom') {
  return {
    top: 'to top',
    right: 'to right',
    bottom: 'to bottom',
    left: 'to left',
  }[value];
}

export default function cssStyles(theme?: Theme) {
  return {
    bgBlur: (props?: BackgroundBlurProps) => {
      const color = props?.color || theme?.palette.background.default || '#000000';

      const blur = props?.blur || 6;
      const opacity = props?.opacity || 0.8;

      return {
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`, // Fix on Mobile
        backgroundColor: alpha(color, opacity),
      };
    },
    bgGradient: (props?: BackgroundGradientProps) => {
      const direction = getDirection(props?.direction);
      const startColor = props?.startColor || `${alpha('#000000', 0)} 0%`;
      const endColor = props?.endColor || '#000000 75%';

      return {
        background: `linear-gradient(${direction}, ${startColor}, ${endColor});`,
      };
    },
    bgImage: (props?: BackgroundImageProps) => {
      const url = props?.url || 'https://minimal-assets-api.vercel.app/assets/images/bg_gradient.jpg';
      const direction = getDirection(props?.direction);
      const startColor = props?.startColor || alpha(theme?.palette.grey[900] || '#000000', 0.88);
      const endColor = props?.endColor || alpha(theme?.palette.grey[900] || '#000000', 0.88);

      return {
        background: `linear-gradient(${direction}, ${startColor}, ${endColor}), url(${url})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
      };
    },
  };
}

/**
 * Merge multiple `sx` props.
 *
 * Sx can accept an array of styles which can be used to merge multiple styles without having the objects overwrite each others keys.
 *
 * @param sx - Multiple `sx` props.
 * @returns The merged `sx` prop.
 *
 * @example
 * ```tsx
 * <Box sx={mergeSx({ color: 'primary.main' }, { fontSize: 16 })} />
 * ```
 */
export const mergeSx = (...sx: (SxProps<Theme> | null | undefined)[]): SxProps<Theme> => {
  return sx
    .filter((style) => style !== null && style !== undefined)
    .flatMap((style) => {
      if (Array.isArray(style)) {
        return [...style];
      }

      return style;
    });
};

/** The horizontal padding a page's content column is laid out with, in theme spacing units. */
const PAGE_GUTTER_X = { xs: 2, sm: 3 };

/**
 * Lets a background span the full page width from inside the content column: the negative margin
 * cancels the page gutter, the matching padding puts the content back where it was. Both halves live
 * here so they cannot drift apart.
 *
 * @example
 * ```tsx
 * <Box sx={{ bgcolor: 'background.paper', ...fullBleedPageGutter }} />
 * ```
 */
export const fullBleedPageGutter = {
  mx: mapValues(PAGE_GUTTER_X, (padding) => -padding),
  px: PAGE_GUTTER_X,
};
